import { useState } from "react";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";

export default function LicenseChecker() {
  const [key, setKey]       = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState("");

  const STATUS_COLOR = {
    active:   "#10b981",
    lifetime: "#22d3ee",
    expired:  "#ef4444",
    revoked:  "#ef4444",
    pending:  "#f59e0b",
  };

  const STATUS_ICON = {
    active:   "✅",
    lifetime: "♾️",
    expired:  "⏰",
    revoked:  "🚫",
    pending:  "⏳",
  };

  async function checkKey() {
    const k = key.trim().toUpperCase();
    if (!k) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const res = await fetch(`${API}/adminrex/check/${k}`);
      if (res.status === 404) {
        setError("Key not found. Check the key and try again.");
      } else if (!res.ok) {
        setError("Error checking key. Please try again.");
      } else {
        setResult(await res.json());
      }
    } catch {
      setError("Cannot connect to server. Please try again later.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section style={{
      background: "linear-gradient(135deg, #0a0b14 0%, #0f1028 100%)",
      padding: "60px 20px",
      textAlign: "center",
    }}>
      <h2 style={{ color: "#eef0ff", fontSize: 28, fontWeight: 700, marginBottom: 8 }}>
        🔑 Check Your License
      </h2>
      <p style={{ color: "#6b6f8e", marginBottom: 32 }}>
        Enter your license key to view status, tool and expiry
      </p>

      <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap" }}>
        <input
          value={key}
          onChange={e => setKey(e.target.value)}
          onKeyDown={e => e.key === "Enter" && checkKey()}
          placeholder="REX-XX-XXXX-XXXX-XXXX"
          style={{
            background: "#13141f",
            border: "1px solid #252738",
            borderRadius: 10,
            color: "#a78bfa",
            fontFamily: "Consolas, monospace",
            fontSize: 14,
            padding: "12px 20px",
            width: 300,
            outline: "none",
            letterSpacing: 1,
          }}
        />
        <button
          onClick={checkKey}
          disabled={loading}
          style={{
            background: loading
              ? "#252738"
              : "linear-gradient(90deg, #6D28D9, #4f7cff)",
            border: "none",
            borderRadius: 10,
            color: "#fff",
            cursor: loading ? "not-allowed" : "pointer",
            fontSize: 14,
            fontWeight: 700,
            padding: "12px 28px",
          }}
        >
          {loading ? "Checking…" : "Check Key"}
        </button>
      </div>

      {error && (
        <p style={{ color: "#ef4444", marginTop: 20, fontSize: 14 }}>{error}</p>
      )}

      {result && (
        <div style={{
          background: "#13141f",
          border: `1px solid ${STATUS_COLOR[result.status] || "#252738"}`,
          borderRadius: 14,
          display: "inline-block",
          marginTop: 28,
          padding: "24px 36px",
          textAlign: "left",
          minWidth: 320,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <span style={{ fontSize: 28 }}>{STATUS_ICON[result.status] || "🔑"}</span>
            <div>
              <div style={{ color: "#eef0ff", fontWeight: 700, fontSize: 16 }}>
                {result.customer}
              </div>
              <div style={{
                color: STATUS_COLOR[result.status] || "#a8acc8",
                fontSize: 12,
                fontWeight: 600,
                textTransform: "uppercase",
              }}>
                {result.status}
              </div>
            </div>
          </div>

          {[
            ["🛠 Tool",     result.tool],
            ["📅 Created",  result.created],
            ["⏱ Expires",  result.expires === "—" ? "Lifetime" : result.expires],
            ["🔑 Key",      result.key],
          ].map(([label, val]) => (
            <div key={label} style={{ display: "flex", gap: 12, marginBottom: 8 }}>
              <span style={{ color: "#6b6f8e", fontSize: 12, minWidth: 80 }}>{label}</span>
              <span style={{
                color: label === "🔑 Key" ? "#a78bfa" : "#eef0ff",
                fontFamily: label === "🔑 Key" ? "Consolas, monospace" : "inherit",
                fontSize: 13,
              }}>{val}</span>
            </div>
          ))}

          {result.devs?.length > 0 && (
            <div style={{ display: "flex", gap: 12, marginTop: 4 }}>
              <span style={{ color: "#6b6f8e", fontSize: 12, minWidth: 80 }}>📱 Devices</span>
              <span style={{ color: "#eef0ff", fontSize: 12 }}>
                {result.devs.join(", ")}
              </span>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
